import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Paper,
  TextField
} from '@mui/material';
// Icon imports removed as they are now part of the components
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import MarkdownRenderer from '../common/MarkdownRenderer';
// parseMarkdownUserStories no longer needed — using common parser via useUserStoryVersions
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useUserStoryVersions from '../../hooks/useUserStoryVersions';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
// Import full screen functionality
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import LoadingBox from '../atoms/LoadingBox';
import { capitalizeFirst } from '../../utils/textTransformations';
import conversationService from '../../services/conversationService';
import AddSectionModal from '../atoms/AddSectionModal';
import { analyzeContentFields, generateContentFromFields } from '../../utils/contentFieldParser';
import useImprove from '../../hooks/useImprove';
import InlineImproveInput from '../common/InlineImproveInput';
import useParsedSections from '../../hooks/useParsedSections';

// Helper function to get story content — now returns markdown directly from the parsed section
const buildStoryContent = (story) => {
  return story.content || story.rawContent || '';
};

/**
 * A split-view layout component that shows conversation on the left
 * and parsed user stories in accordion format on the right
 */
const SplitViewUserStories = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const [showAddStoryModal, setShowAddStoryModal] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const { showNotification } = useNotification();
  const storyPanelRef = useRef(null);
  // Helper function to format story titles to sentence case
  const formatStoryTitleToSentenceCase = (title) => {
    if (!title) return title;

    // Handle US001: pattern - capitalize first word after colon
    const colonMatch = title.match(/^(US\d+:\s*)(.+)$/);
    if (colonMatch) {
      const prefix = colonMatch[1];
      const content = colonMatch[2];
      // Use capitalizeFirst utility for the content part
      return prefix + capitalizeFirst(content);
    }

    // For other titles, use capitalizeFirst utility
    return capitalizeFirst(title);
  };

  // Track if we're doing a local update (passed to CRUD hooks)
  const isLocalUpdateRef = useRef(false);

  // Use version management for stories
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useUserStoryVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedStories,
    setSections: setParsedStories,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef: storyContentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing expanded/visible stories
  const [expandedStories, setExpandedStories] = useState({});
  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedStories, setExpandedStories);

  const [visibleStories, setVisibleStories] = useState(10);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible stories when switching versions
  useEffect(() => {
    if (currentVersion) {
      // Only reset visible stories when actually switching versions
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleStories(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Fetch features from features conversation when modal opens (for Related Feature dropdown)
  useEffect(() => {
    const fetchFeaturesFromConversation = async () => {
      if (showAddStoryModal && projectId) {
        try {
          // Fetch all conversations for the project
          const conversations = await conversationService.getConversations(projectId);

          // Find the features conversation
          const featuresConversation = conversations.find(
            conv => conv.taskId === 'features' || conv.task_id === 'features'
          );

          if (featuresConversation && featuresConversation.messages) {
            // Find the last assistant message with features
            const assistantMessages = featuresConversation.messages.filter(
              msg => msg.role === 'assistant' && msg.content
            );

            // Get the last message that contains features (has ## F0 pattern)
            const featureMessage = assistantMessages.reverse().find(msg =>
              msg.content.includes('## F0') ||
              msg.content.includes('**Priority:**') ||
              msg.content.includes('**Complexity:**')
            );

            if (featureMessage) {
              // Parse features from the message content
              const { parseMarkdownIntoSections } = await import('../../utils/markdownSectionParser');
              const parsedFeatures = parseMarkdownIntoSections(featureMessage.content);
              setAvailableFeatures(parsedFeatures);
            } else {
              setAvailableFeatures([]);
            }
          } else {
            setAvailableFeatures([]);
          }
        } catch (error) {
          console.error('Error fetching features from conversation:', error);
          setAvailableFeatures([]);
        }
      }
    };
    fetchFeaturesFromConversation();
  }, [showAddStoryModal, projectId]);

  // Check if this is a user stories conversation
  const isUserStoriesConversation = conversation?.taskId === 'user-stories' ||
    (conversation?.messages?.some(msg =>
      msg.role === 'assistant' &&
      (msg.content?.includes('## US') || msg.content?.includes('As a '))
    ));

  const showStoryPanel = parsedStories.length > 0;

  const handleStoryToggle = (storyId) => {
    setExpandedStories(prev => ({
      ...prev,
      [storyId]: !prev[storyId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleStories(prev => Math.min(prev + 10, parsedStories.length));
  };

  const handleShowAll = () => {
    setVisibleStories(parsedStories.length);
  };

  // Use CRUD operations hook
  const {
    editingSection: editingStory,
    editedContent,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    editedTitle,
    handleStartEdit: handleStartEditOriginal,
    handleCancelEdit,
    handleSaveEdit: handleSaveEditOriginal,
    handleDeleteSection: handleDeleteStoryClick,
    handleDeleteSectionConfirmed: handleDeleteConfirmBase,
    handleExplainSection: handleExplainStoryOriginal,
    handleCopySection: handleCopyStoryOriginal
  } = useSectionCRUD(conversation, parsedStories, setParsedStories, setExpandedStories, storyContentRef, updateCurrentVersion, currentVersion, isLocalUpdateRef,
    null, // onConversationUpdate
    markLocalEdit
  );

  // Custom delete handler that properly reconstructs user story markdown
  // Renumbers stories sequentially after deletion (US001, US002, US003, ...)
  const handleDeleteConfirm = async () => {
    const story = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!story) return;

    try {
      // Remove the story from the stories array (keep original IDs, no renumbering)
      const updatedStories = parsedStories.filter(s => s.id !== story.id);

      // Recalculate isFirstInCategory for remaining stories
      const seenCategories = new Set();
      updatedStories.forEach(s => {
        if (s.category && !seenCategories.has(s.category)) {
          s.isFirstInCategory = true;
          seenCategories.add(s.category);
        } else {
          s.isFirstInCategory = false;
        }
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedStories(updatedStories);

      // Remove expanded state for the deleted story
      setExpandedStories(prev => {
        const newExpanded = { ...prev };
        delete newExpanded[story.id];
        return newExpanded;
      });

      // Save sections as JSON — backend reconstructs markdown and syncs to conversation
      const messageTimestamp = currentVersion?.timestamp;
      let updatedContent = '';

      if (messageTimestamp && conversation._id) {
        try {
          const result = await conversationService.updateParsedSections(
            conversation._id,
            messageTimestamp,
            updatedStories
          );
          updatedContent = result?.data?.markdown || '';
        } catch (err) {
          console.warn('JSON save failed on delete, falling back:', err);
        }
      }

      storyContentRef.current = updatedContent.trim();

      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          stories: updatedStories,
          parsedContent: updatedStories
        });
      }

      // Update LHS
      if (onConversationUpdate && conversation._id) {
        const updatedMessages = [...conversation.messages];
        if (currentVersion?.messageIndex !== undefined) {
          const idx = currentVersion.messageIndex;
          if (updatedMessages[idx]?.role === 'assistant') {
            updatedMessages[idx] = { ...updatedMessages[idx], content: updatedContent.trim() };
          }
        }
        await onConversationUpdate(conversation._id, updatedMessages);
      }

      showNotification({
        message: `"${story.title}" deleted successfully`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting story:', error);
      showNotification({
        message: `Failed to delete "${story.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    isSaving: isSavingDocument,
    handleExport: handleExportStories,
    getExportContent,
    handleCopy: handleCopyStories,
    handleRefresh: handleRefreshStories
  } = useContentActions(storyContentRef, 'User Stories', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state for stories with version-specific IDs
  const initialValidationState = parsedStories.reduce((acc, story) => {
    const storyId = story.id || `story-${parsedStories.indexOf(story)}`;
    // Create version-specific validation ID
    const versionSpecificId = `${storyId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedStories,
    handleValidateItem: handleValidateStoryBase,
    isLoading: isLoadingValidations,
    setValidatedItems: setValidatedStories,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'user-stories',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'user-stories',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateStory = async (storyId) => {
    // Create version-specific ID
    const versionSpecificId = `${storyId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateStoryBase(versionSpecificId);
  };

  // Create wrapper functions to maintain compatibility with existing code
  const handleStartEdit = (storyId, story) => {
    // Extract content for editing using buildStoryContent helper
    const contentToEdit = buildStoryContent(story);
    console.log('contentToEdit', contentToEdit);
    // Pass the title for editing as well
    handleStartEditOriginal(storyId, contentToEdit, story.title || '');

    // Old code - commented out, now using buildStoryContent helper
    // let contentToEdit = '';
    // if (story.asA) {
    //   contentToEdit += story.asA + '\n\n';
    // }
    // if (story.sections?.description) {
    //   contentToEdit += '**Description**: ' + story.sections.description + '\n\n';
    // }
    // if (story.sections?.['acceptance criteria']) {
    //   contentToEdit += '**Acceptance Criteria**:\n';
    //   if (Array.isArray(story.sections['acceptance criteria'])) {
    //     story.sections['acceptance criteria'].forEach(criteria => {
    //       contentToEdit += '- ' + criteria + '\n';
    //     });
    //   } else {
    //     contentToEdit += story.sections['acceptance criteria'] + '\n';
    //   }
    //   contentToEdit += '\n';
    // }
    // if (story.sections?.priority) {
    //   contentToEdit += '**Priority**: ' + story.sections.priority + '\n\n';
    // }
    // if (story.sections?.['related feature']) {
    //   contentToEdit += '**Related Feature**: ' + story.sections['related feature'] + '\n\n';
    // }
    // if (story.sections?.['story points']) {
    //   contentToEdit += '**Story Points**: ' + story.sections['story points'] + '\n\n';
    // }
    // handleStartEditOriginal(storyId, contentToEdit.trim());
  };

  const handleSaveEdit = async (storyId) => {
    try {
      // We'll use the isSaving state from the hook, which is already being set

      // Find the story being edited
      const storyIndex = parsedStories.findIndex(s => s.id === storyId);
      if (storyIndex === -1) return;

      // Parse the edited content back into structured format
      // Fields order: actor(s), description, trigger, preconditions, postconditions, normal flow, alternative flows & exceptions, acceptance criteria, priority, related feature
      const lines = editedContent.split('\n');
      let currentSection = '';
      let parsedContent = {
        asA: '',
        sections: {
          'actor(s)': '',
          description: '',
          trigger: '',
          preconditions: [],
          postconditions: [],
          'normal flow': [],
          'alternative flows & exceptions': [],
          'acceptance criteria': [],
          priority: '',
          'related feature': ''
        }
      };

      // List-type sections that support array items
      const listSections = ['preconditions', 'postconditions', 'alternative flows & exceptions', 'acceptance criteria'];
      const numberedListSections = ['normal flow'];

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        if (line.startsWith('As a ') && !currentSection) {
          parsedContent.asA = line;
        } else if (line.startsWith('**Actor(s)**:')) {
          currentSection = 'actor(s)';
          parsedContent.sections['actor(s)'] = line.replace('**Actor(s)**:', '').trim();
        } else if (line.startsWith('**Description**:')) {
          currentSection = 'description';
          parsedContent.sections.description = line.replace('**Description**:', '').trim();
        } else if (line.startsWith('**Trigger**:')) {
          currentSection = 'trigger';
          parsedContent.sections.trigger = line.replace('**Trigger**:', '').trim();
        } else if (line.startsWith('**Preconditions**:')) {
          currentSection = 'preconditions';
        } else if (line.startsWith('**Postconditions**:')) {
          currentSection = 'postconditions';
        } else if (line.startsWith('**Normal Flow**:')) {
          currentSection = 'normal flow';
        } else if (line.startsWith('**Alternative Flows & Exceptions**:')) {
          currentSection = 'alternative flows & exceptions';
        } else if (line.startsWith('**Acceptance Criteria**:')) {
          currentSection = 'acceptance criteria';
        } else if (line.startsWith('**Priority**:')) {
          currentSection = 'priority';
          parsedContent.sections.priority = line.replace('**Priority**:', '').trim();
        } else if (line.startsWith('**Related Feature**:')) {
          currentSection = 'related feature';
          parsedContent.sections['related feature'] = line.replace('**Related Feature**:', '').trim();
        } else if (listSections.includes(currentSection) && line.startsWith('-')) {
          parsedContent.sections[currentSection].push(line.substring(1).trim());
        } else if (numberedListSections.includes(currentSection) && /^\d+\./.test(line)) {
          // Handle numbered list items (e.g., "1. Step one")
          parsedContent.sections[currentSection].push(line.replace(/^\d+\.\s*/, '').trim());
        } else if (currentSection && line) {
          // Append to current section if it's not a list item
          if (!listSections.includes(currentSection) && !numberedListSections.includes(currentSection)) {
            parsedContent.sections[currentSection] += (parsedContent.sections[currentSection] ? ' ' : '') + line;
          }
        }
      }

      // console.log('parsedContent', parsedContent);



      // Update the story with parsed content, preserving category info
      // Use editedTitle if it was changed, otherwise keep the original title
      const updatedStories = [...parsedStories];
      updatedStories[storyIndex] = {
        ...updatedStories[storyIndex],
        asA: parsedContent.asA || updatedStories[storyIndex].asA,
        // Use editedTitle if provided, otherwise fall back to existing title
        title: editedTitle || updatedStories[storyIndex].title,
        sections: { ...updatedStories[storyIndex].sections, ...parsedContent.sections },
        // Preserve category information
        category: updatedStories[storyIndex].category,
        isFirstInCategory: updatedStories[storyIndex].isFirstInCategory,
        // Update content/rawContent so buildStoryContent and backend use the new content
        content: editedContent,
        rawContent: editedContent
      };

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedStories(updatedStories);

      // Reconstruct the full content with updated story, including category headers
      let updatedContent = '';
      let lastCategory = null;

      updatedStories.forEach((story, idx) => {
        // Add category header if this is the first story in a new category
        if (story.isFirstInCategory && story.category && story.category !== lastCategory) {
          if (idx > 0) updatedContent += '\n';
          updatedContent += `# ${story.category}\n\n`;
          lastCategory = story.category;
        }

        updatedContent += `## ${story.title}\n\n`;
        // Use buildStoryContent helper for consistency
        updatedContent += buildStoryContent(story) + '\n\n';
        if (idx !== updatedStories.length - 1) {
          updatedContent += '---\n\n';
        }
      });

      // Save sections as JSON — backend reconstructs markdown and syncs to conversation
      const messageTimestamp = currentVersion?.timestamp;
      if (messageTimestamp && conversation._id) {
        try {
          const result = await conversationService.updateParsedSections(
            conversation._id,
            messageTimestamp,
            updatedStories
          );
          updatedContent = result?.data?.markdown || updatedContent;
        } catch (err) {
          console.warn('JSON save failed on edit, falling back to markdown:', err);
        }
      }

      storyContentRef.current = updatedContent.trim();

      // Update the current version with new content using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          stories: updatedStories,
          parsedContent: updatedStories
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ UserStories Edit: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserStories Edit: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserStories Edit: Called onConversationUpdate');
      }

      // Exit edit mode
      handleCancelEdit();
      setEditedContent('');
      setEditedTitle('');

      showNotification({
        message: 'User story updated successfully',
        type: 'success'
      });
    } catch (error) {
      // console.error('Error saving story:', error);
      showNotification({
        message: 'Failed to save changes',
        type: 'error'
      });
    } finally {
      // isSaving state is managed by the hook
    }
  };

  const handleCopyStory = (story) => {
    // Build the full story content with title
    let storyContent = `${story.title}\n`;
    storyContent += `${'-'.repeat(story.title.length)}\n\n`;
    storyContent += buildStoryContent(story);

    // Create story object with content property for the copy handler
    const storyWithContent = {
      ...story,
      content: storyContent
    };

    return handleCopyStoryOriginal(storyWithContent);
  };

  const handleExplainStory = (story) => {
    requestExplanation(story);
  };

  const handleImproveStory = (story) => {
    // Build the story content using helper function
    const storyContent = buildStoryContent(story);

    // Create a story object with content property
    const storyWithContent = {
      ...story,
      content: storyContent,
      rawContent: storyContent,
      type: 'user-story'
    };

    handleImproveClick(storyWithContent);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      // Use merged content if it's a specific section improvement
      const contentToUse = improveSection.mergedContent || improvedContent;

      // Update the story with improved content
      // Fields order: actor(s), description, trigger, preconditions, postconditions, normal flow, alternative flows & exceptions, acceptance criteria, priority, related feature
      const updatedStories = [...parsedStories].map(s => {
        if (s.id === improveSection.id) {
          // Parse the improved content to extract structured data
          const lines = contentToUse.split('\n');
          let currentSection = '';
          let parsedContent = {
            asA: '',
            sections: {
              'actor(s)': '',
              description: '',
              trigger: '',
              preconditions: [],
              postconditions: [],
              'normal flow': [],
              'alternative flows & exceptions': [],
              'acceptance criteria': [],
              priority: '',
              'related feature': ''
            }
          };

          // List-type sections that support array items
          const listSections = ['preconditions', 'postconditions', 'alternative flows & exceptions', 'acceptance criteria'];
          const numberedListSections = ['normal flow'];

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();

            if (line.startsWith('As a ') && !currentSection) {
              parsedContent.asA = line;
            } else if (line.startsWith('**Actor(s)**:')) {
              currentSection = 'actor(s)';
              parsedContent.sections['actor(s)'] = line.replace('**Actor(s)**:', '').trim();
            } else if (line.startsWith('**Description**:')) {
              currentSection = 'description';
              parsedContent.sections.description = line.replace('**Description**:', '').trim();
            } else if (line.startsWith('**Trigger**:')) {
              currentSection = 'trigger';
              parsedContent.sections.trigger = line.replace('**Trigger**:', '').trim();
            } else if (line.startsWith('**Preconditions**:')) {
              currentSection = 'preconditions';
            } else if (line.startsWith('**Postconditions**:')) {
              currentSection = 'postconditions';
            } else if (line.startsWith('**Normal Flow**:')) {
              currentSection = 'normal flow';
            } else if (line.startsWith('**Alternative Flows & Exceptions**:')) {
              currentSection = 'alternative flows & exceptions';
            } else if (line.startsWith('**Acceptance Criteria**:')) {
              currentSection = 'acceptance criteria';
            } else if (line.startsWith('**Priority**:')) {
              currentSection = 'priority';
              parsedContent.sections.priority = line.replace('**Priority**:', '').trim();
            } else if (line.startsWith('**Related Feature**:')) {
              currentSection = 'related feature';
              parsedContent.sections['related feature'] = line.replace('**Related Feature**:', '').trim();
            } else if (listSections.includes(currentSection) && line.startsWith('-')) {
              parsedContent.sections[currentSection].push(line.substring(1).trim());
            } else if (numberedListSections.includes(currentSection) && /^\d+\./.test(line)) {
              // Handle numbered list items (e.g., "1. Step one")
              parsedContent.sections[currentSection].push(line.replace(/^\d+\.\s*/, '').trim());
            } else if (currentSection && line) {
              // Append to current section if it's not a list item
              if (!listSections.includes(currentSection) && !numberedListSections.includes(currentSection)) {
                parsedContent.sections[currentSection] += (parsedContent.sections[currentSection] ? ' ' : '') + line;
              }
            }
          }

          // Old parsing code - commented out
          // let parsedContent = {
          //   asA: '',
          //   sections: {
          //     description: '',
          //     'acceptance criteria': [],
          //     priority: '',
          //     'related feature': '',
          //     'story points': ''
          //   }
          // };
          //
          // for (let i = 0; i < lines.length; i++) {
          //   const line = lines[i].trim();
          //
          //   if (line.startsWith('As a ') && !currentSection) {
          //     parsedContent.asA = line;
          //   } else if (line.startsWith('**Description**:')) {
          //     currentSection = 'description';
          //     parsedContent.sections.description = line.replace('**Description**:', '').trim();
          //   } else if (line.startsWith('**Acceptance Criteria**:')) {
          //     currentSection = 'acceptance criteria';
          //   } else if (line.startsWith('**Priority**:')) {
          //     currentSection = 'priority';
          //     parsedContent.sections.priority = line.replace('**Priority**:', '').trim();
          //   } else if (line.startsWith('**Related Feature**:')) {
          //     currentSection = 'related feature';
          //     parsedContent.sections['related feature'] = line.replace('**Related Feature**:', '').trim();
          //   } else if (line.startsWith('**Story Points**:')) {
          //     currentSection = 'story points';
          //     parsedContent.sections['story points'] = line.replace('**Story Points**:', '').trim();
          //   } else if (currentSection === 'acceptance criteria' && line.startsWith('-')) {
          //     parsedContent.sections['acceptance criteria'].push(line.substring(1).trim());
          //   } else if (currentSection && line) {
          //     // Append to current section if it's not a list item
          //     if (currentSection !== 'acceptance criteria') {
          //       parsedContent.sections[currentSection] += (parsedContent.sections[currentSection] ? ' ' : '') + line;
          //     }
          //   }
          // }

          return {
            ...s,
            asA: parsedContent.asA || s.asA,
            title: parsedContent.asA || s.title,
            sections: { ...s.sections, ...parsedContent.sections },
            // Preserve category information
            category: s.category,
            isFirstInCategory: s.isFirstInCategory
          };
        }
        return s;
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedStories(updatedStories);

      // Reconstruct the full content with updated story, including category headers
      let updatedContent = '';
      let lastCategory = null;

      updatedStories.forEach((story, idx) => {
        // Add category header if this is the first story in a new category
        if (story.isFirstInCategory && story.category && story.category !== lastCategory) {
          if (idx > 0) updatedContent += '\n';
          updatedContent += `# ${story.category}\n\n`;
          lastCategory = story.category;
        }

        updatedContent += `## ${story.title}\n\n`;
        // Use buildStoryContent helper for consistency
        updatedContent += buildStoryContent(story) + '\n\n';

        // Old content reconstruction - commented out
        // if (story.asA) updatedContent += `${story.asA}\n\n`;
        // if (story.sections?.description) updatedContent += `**Description**: ${story.sections.description}\n\n`;
        // if (story.sections?.['acceptance criteria']) {
        //   updatedContent += `**Acceptance Criteria**:\n`;
        //   if (Array.isArray(story.sections['acceptance criteria'])) {
        //     story.sections['acceptance criteria'].forEach(criteria => {
        //       updatedContent += `- ${criteria}\n`;
        //     });
        //   } else {
        //     updatedContent += `${story.sections['acceptance criteria']}\n`;
        //   }
        //   updatedContent += '\n';
        // }
        // if (story.sections?.priority) updatedContent += `**Priority**: ${story.sections.priority}\n\n`;
        // if (story.sections?.['related feature']) updatedContent += `**Related Feature**: ${story.sections['related feature']}\n\n`;

        if (idx !== updatedStories.length - 1) {
          updatedContent += '---\n\n';
        }
      });

      storyContentRef.current = updatedContent.trim();

      // Update the current version with new stories using the hook's update function
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          stories: updatedStories,  // Update the stories array
          parsedContent: updatedStories  // Also update parsedContent to match the hook's expectation
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent.trim()
          };
          messageUpdated = true;
          console.log('✅ UserStories Improve: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserStories Improve: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserStories Improve: Called onConversationUpdate');
      }

      closeImprovementModal();

      showNotification({
        message: 'User story improved successfully',
        type: 'success'
      });
    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Handler for adding new story
  // Fields order: actor(s), description, trigger, preconditions, postconditions, normal flow, alternative flows & exceptions, acceptance criteria, priority, related feature
  const handleAddNewStory = async (newSectionData) => {
    try {
      // Generate story format for display
      // Since we renumber on delete, we can simply use length + 1
      const storyNumber = parsedStories.length + 1;
      const formattedStoryId = `US${String(storyNumber).padStart(3, '0')}`;

      // Parse the content to extract structured data
      let parsedSections = {
        'actor(s)': '',
        description: '',
        trigger: '',
        preconditions: [],
        postconditions: [],
        'normal flow': [],
        'alternative flows & exceptions': [],
        'acceptance criteria': [],
        priority: 'Medium',
        'related feature': ''
      };

      // List-type sections that support array items
      const listSections = ['preconditions', 'postconditions', 'alternative flows & exceptions', 'acceptance criteria'];
      const numberedListSections = ['normal flow'];

      // Try to parse structured content from the input
      const lines = newSectionData.content.split('\n');
      let currentSection = '';

      for (const line of lines) {
        const trimmedLine = line.trim();

        // Match patterns like **Actor(s)**: or Actor(s): (with or without asterisks)
        if (/\*?\*?actor\(s\)\*?\*?:/i.test(trimmedLine) || /\*?\*?actors\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'actor(s)';
          parsedSections['actor(s)'] = trimmedLine.replace(/\*?\*?actor\(s\)\*?\*?:\s*/i, '').replace(/\*?\*?actors\*?\*?:\s*/i, '').trim();
        } else if (/\*?\*?description\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'description';
          parsedSections.description = trimmedLine.replace(/\*?\*?description\*?\*?:\s*/i, '').trim();
        } else if (/\*?\*?trigger\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'trigger';
          parsedSections.trigger = trimmedLine.replace(/\*?\*?trigger\*?\*?:\s*/i, '').trim();
        } else if (/\*?\*?preconditions\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'preconditions';
        } else if (/\*?\*?postconditions\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'postconditions';
        } else if (/\*?\*?normal flow\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'normal flow';
        } else if (/\*?\*?alternative flows & exceptions\*?\*?:/i.test(trimmedLine) || /\*?\*?alternative flows\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'alternative flows & exceptions';
        } else if (/\*?\*?acceptance criteria\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'acceptance criteria';
        } else if (/\*?\*?priority\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'priority';
          parsedSections.priority = trimmedLine.replace(/\*?\*?priority\*?\*?:\s*/i, '').trim();
        } else if (/\*?\*?related feature\*?\*?:/i.test(trimmedLine)) {
          currentSection = 'related feature';
          parsedSections['related feature'] = trimmedLine.replace(/\*?\*?related feature\*?\*?:\s*/i, '').trim();
        } else if (listSections.includes(currentSection) && trimmedLine.startsWith('-')) {
          parsedSections[currentSection].push(trimmedLine.substring(1).trim());
        } else if (numberedListSections.includes(currentSection) && /^\d+\./.test(trimmedLine)) {
          // Handle numbered list items (e.g., "1. Step one")
          parsedSections[currentSection].push(trimmedLine.replace(/^\d+\.\s*/, '').trim());
        } else if (currentSection && trimmedLine) {
          if (!listSections.includes(currentSection) && !numberedListSections.includes(currentSection)) {
            parsedSections[currentSection] += (parsedSections[currentSection] ? ' ' : '') + trimmedLine;
          }
        }
      }

      // If no acceptance criteria found, use the whole content as description
      if (!parsedSections.description && parsedSections['acceptance criteria'].length === 0) {
        parsedSections.description = newSectionData.content;
      }

      const displayStory = {
        id: formattedStoryId,
        title: newSectionData.title,
        sections: parsedSections
      };


      // Only add to the latest version
      if (!isLatestVersion) {
        showNotification({
          message: 'Please switch to the latest version to add new stories',
          type: 'warning'
        });
        setShowAddStoryModal(false);
        return;
      }

      // Add the new story to the current stories
      const updatedStories = [...parsedStories, displayStory];

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedStories(updatedStories);

      // Expand the new story by default
      setExpandedStories(prev => ({ ...prev, [displayStory.id]: true }));

      // Ensure the new story is visible if we're using pagination
      if (visibleStories < updatedStories.length) {
        setVisibleStories(updatedStories.length);
      }

      // Update conversation messages with new story using buildStoryContent helper
      let storyText = `## ${displayStory.id}: ${displayStory.title}\n\n`;
      storyText += buildStoryContent(displayStory);

      const updatedContent = storyContentRef.current + '\n\n---\n\n' + storyText.trim();

      // Update content ref
      storyContentRef.current = updatedContent;



      // Save to backend
      const updatedMessages = [...conversation.messages];
      let messageUpdated = false;

      // Try to match by messageIndex first (most reliable for V1)
      if (currentVersion && currentVersion.messageIndex !== undefined) {
        const idx = currentVersion.messageIndex;
        if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
          updatedMessages[idx] = {
            ...updatedMessages[idx],
            content: updatedContent
          };
          messageUpdated = true;
          console.log('✅ UserStories Add: Updated message by messageIndex at index', idx);
        }
      }

      // Fallback: update the last assistant message if we couldn't find by messageIndex
      if (!messageUpdated) {
        console.warn('⚠️ UserStories Add: No messageIndex match, updating last assistant message');
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };
      await conversationService.saveConversation(conversation._id, conversationData);

      showNotification({
        message: 'User story added successfully',
        type: 'success'
      });

      // Close modal after successful save
      setShowAddStoryModal(false);

      // Update the current version with the new stories
      if (updateCurrentVersion && currentVersion) {
        const updatedVersion = {
          ...currentVersion,
          stories: updatedStories,
          content: updatedContent.trim(),
          parsedContent: updatedStories
        };
        updateCurrentVersion(updatedVersion);
      }

      // Update LHS immediately by calling onConversationUpdate with updated messages
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ UserStories Add: Called onConversationUpdate');
      }

      // Scroll to the new story
      setTimeout(() => {
        if (storyPanelRef.current) {
          storyPanelRef.current.scrollTop = storyPanelRef.current.scrollHeight;
        }
      }, 100);
    } catch (error) {
      showNotification({
        message: 'Failed to add new user story',
        type: 'error'
      });
    }
  };


  // Auto-scroll story panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && storyPanelRef.current && isUserStoriesConversation) {
      // Wait a bit to ensure content renders before scrolling
      setTimeout(() => {
        if (storyPanelRef.current) {
          storyPanelRef.current.scrollTop = storyPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isUserStoriesConversation]);



  // Don't show the split view if this isn't a user story conversation
  // or if no stories are found and we're not streaming
  if (!isUserStoriesConversation || (!showStoryPanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>

      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* User stories accordion panel */}
        <Box
          ref={storyPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='User Stories' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      console.log('🔄 UserStories: Version changed - reset isLocalUpdateRef');
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  onAdd={isLatestVersion ? () => setShowAddStoryModal(true) : null}
                  getExportContent={getExportContent}
                  onExport={handleExportStories}
                  isExporting={isExporting}
                  onCopy={handleCopyStories}
                  onRefresh={handleRefreshStories}
                  disabled={!storyContentRef.current}
                  addTooltip={!isLatestVersion ? "Switch to latest version to add stories" : "Add new user story"}
                  exportTooltip="Export user stories as markdown"
                  copyTooltip="Copy user stories to clipboard"
                  refreshTooltip="Refresh user stories"
                />
              </Box>
            </Box>

            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId={'user-stories'}
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedStories}
              totalItems={parsedStories?.length || 0}
                currentUserMemberType={currentUserMemberType}
            />
            <Box sx={{
              p: 3,
              pt: 2,
              flex: 1,
              overflow: "auto",
              '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
                marginTop: '5px',
                marginBottom: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.4)',
                },
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: 'transparent',
              },
            }}>
              {/* Loading while generating */}
              {isStreaming ? (
                <LoadingBox message='Generating new user stories...' spinnerSize={40} />
              ) : showStoryPanel && parsedStories.length > 0 ? (
                <Box>
                  {/* Render stories as accordions using the hook-based approach */}
                  {parsedStories.slice(0, visibleStories).map((story, index) => {
                    const storyId = story.id || `story-${index}`;
                    // Use version-specific ID for validation check
                    const versionSpecificId = `${storyId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                    const isValidated = validatedStories[versionSpecificId] || false;

                    // Debug logging for category rendering
                    {/* if (story.isFirstInCategory) {
                      console.log(`Story ${story.id} is first in category: ${story.category}`);
                    } */}

                    return (
                      <React.Fragment key={storyId}>
                        {/* Display category title if this is the first story in a category */}
                        {story.isFirstInCategory && story.category && (
                          <Box sx={{
                            mt: index === 0 ? 0 : 3,
                            mb: 2,
                            px: 2
                          }}>
                            <SectionHeader label={story.category} />
                          </Box>
                        )}

                        <Accordion
                          ref={(el) => { registerSectionRef(storyId, el); if (shouldHighlight(storyId)) highlightRef.current = el; }}
                          title={story.title || 'User Story'}
                          defaultExpanded={false}
                          expanded={expandedStories[storyId]}
                          onChange={(_, expanded) => handleStoryToggle(storyId)}
                          titleContent={editingStory === storyId ? (
                            <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                              <TextField
                                value={editedTitle}
                                onChange={(e) => setEditedTitle(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                onFocus={(e) => e.target.removeAttribute('title')}
                                size="small"
                                fullWidth
                                placeholder="Enter user story title"
                                variant="outlined"
                                InputProps={{ title: '' }}
                                inputProps={{ title: '', autoComplete: 'off' }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    backgroundColor: '#fff',
                                    '& fieldset': { borderWidth: '1px' },
                                    '&:hover fieldset': { borderWidth: '1px' },
                                    '&.Mui-focused fieldset': { borderWidth: '1px' },
                                  },
                                  '& .MuiOutlinedInput-input': {
                                    fontSize: '14px',
                                    fontFamily: 'inherit',
                                    padding: '8px 12px',
                                  },
                                }}
                              />
                            </Box>
                          ) : null}
                          rightIcon={
                            <Box
                              onClick={async (e) => {
                                e.stopPropagation();

                                try {
                                  await handleValidateStory(storyId);
                                } catch (error) {
                                  showNotification({
                                    message: `Failed to update validation for "${story.title}"`,
                                    type: "error"
                                  });
                                }
                              }}
                              sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                            >
                              {isValidated ? (
                                <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                              ) : (
                                <UnvalidatedIcon size={25} />
                              )}
                            </Box>
                          }
                        >
                          {/* Story content with proper formatting */}
                          <ScrollableBox
                            sx={{
                              textAlign: 'justify',
                              cursor: editingStory === storyId ? 'default' : 'pointer',
                            }}
                          >
                            {editingStory === storyId ? (
                              // Edit mode
                              <EditableTextField
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                              />
                            ) : (
                              // Normal content display - format as markdown
                              <Box ref={getSectionContentRef(storyId)}>
                                {/* Build story content as markdown */}
                                <MarkdownRenderer content={buildStoryContent(story)} />
                                <TextSelectionPopup
                                  containerRef={getSectionContentRef(storyId)}
                                  sectionId={storyId}
                                  onSubmitComment={addComment}
                                  disabled={editingStory === storyId}
                                />

                                {/* Inline improve input */}
                                {showImproveInput === storyId && (
                                  <InlineImproveInput
                                    improvePrompt={improvePrompt}
                                    onPromptChange={setImprovePrompt}
                                    onSend={() => {
                                      // Build story content using helper function
                                      const storyContent = buildStoryContent(story);

                                      const storyWithContent = {
                                        ...story,
                                        content: storyContent,
                                        rawContent: storyContent,
                                        type: 'user-story'
                                      };

                                      handleSendImprove(storyWithContent);
                                    }}
                                    onCancel={handleCancelImprove}
                                    promptText="How would you like to improve this user story?"
                                    placeholder="Describe the improvements you'd like..."
                                  />
                                )}
                              </Box>
                            )}
                          </ScrollableBox>

                          {/* Action buttons */}
                          <Box sx={{
                            position: 'absolute',
                            right: 10,
                            top: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5
                          }}>
                            <ActionButtons
                              item={story}
                              itemIndex={index}
                              storyId={storyId}
                              editingStory={editingStory}
                              isSaving={isSaving}
                              onEdit={() => handleStartEdit(storyId, story)}
                              onSave={() => handleSaveEdit(storyId)}
                              onCancel={handleCancelEdit}
                              onImprove={() => handleImproveStory(story)}
                              onExplain={() => handleExplainStory(story)}
                              onCopy={() => handleCopyStory(story)}
                              onDelete={() => handleDeleteStoryClick(story)}
                              onComment={() => openCommentPanel(storyId)}
                              commentCount={commentCounts[storyId]?.open || 0}
                              vertical={true}
                              isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                            />
                          </Box>
                        </Accordion>
                      </React.Fragment>
                    );
                  })}

                  {/* Load more button for lazy loading */}
                  {parsedStories.length > visibleStories && (
                    <LoadMoreButton
                      remaining={parsedStories.length - visibleStories}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  )}
                </Box>
              ) : (
                <EmptyStateBox message="No user stories found in this conversation yet." />
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete user story"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.id}: ${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddStoryModal}
        onClose={() => setShowAddStoryModal(false)}
        onSave={handleAddNewStory}
        sectionType="userStories"
        existingSections={parsedStories.map(story => ({
          ...story,
          content: buildStoryContent(story)
        }))}
        featureOptions={availableFeatures}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />
    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedStories || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewUserStories;